import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import ImageListModal from '../../../../../modules/components/modal/ImageListModal';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { RemarkOptionType } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import { EditSiRemarks } from '../form/EditSiRemarks';
import { HistoryModal } from '../modal/HistoryModal';
import { getFieldUpdate, getImagePathByCaptureId } from '../utils';

interface Props {
  data: any;
  handleRefetch: () => void;
  captures: any[];
  isSiteSaving: boolean;
  isDirty?: boolean;
  handleDirtyChange?: (isDirty: boolean) => void;
  isEditable?: boolean;
  isPublic?: boolean;
}

export default function Remarks({
  data,
  handleRefetch,
  captures,
  isSiteSaving,
  isDirty,
  handleDirtyChange,
  isEditable,
  isPublic = false,
}: Props) {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();

  const formRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [remarkOptions, setRemarkOptions] = useState(undefined);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedImagesData, setSelectedImagesData] =
    useState<{ label: string; captures: { url: string; image: string | File }[] }>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const allFieldUpdates = data?.results?.field_updates;
  const remarkOptionFieldUpdates = getFieldUpdate('remark_options', allFieldUpdates);
  const hasHistory = remarkOptionFieldUpdates.length > 0;

  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_REMARKS.${id}` }, values);

  const submit = async () => {
    if (formRef.current) {
      setIsSaving(true);
      await formRef.current.submit();
      handleRefetch();
      setIsSaving(false);
    }
  };

  const reset = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
  };

  const {
    data: remarkOptionData,
    status,
    refetch,
  } = useQuery(
    ['site-intelligence-remark-options', auth, refresh],
    async () => {
      if (isPublic) {
        try {
          const endpoint = new URL(
            `public/v1/list_remark_options`,
            process.env.REACT_APP_SITE_INTELLIGENCE_BACKEND_SERVICE_BASE_URL
          ).href;

          const { result, status } = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'X-Service-Client': 'cbp',
              'content-type': 'application/json',
            },
            body: JSON.stringify({
              filters: {
                status: ['ACTIVE'],
              },
              sort_order: {
                name: 'created_at',
                sort: -1,
              },
              offset: 0,
              limit: 0,
            }),
          }).then(async (resp) => {
            const result = await resp?.json();
            return { result: result, status: result?.code ?? resp?.status };
          });
          const resolvedResult = await result;
          if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false)
            return resolvedResult;
        } catch (err) {
          const response = await err?.response?.json();
          setToast({
            show: true,
            type: 'Danger',
            header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
            message: response?.message ?? err.message,
          });
        }
      } else {
        try {
          const { result, status } = await api(
            `admin/v1/list_remark_options`,
            {
              method: 'POST',
              headers: {
                'content-type': 'application/json',
              },
              body: JSON.stringify({
                filters: {
                  status: ['ACTIVE'],
                },
                sort_order: {
                  name: 'created_at',
                  sort: -1,
                },
                offset: 0,
                limit: 0,
              }),
            },
            { token: auth?.accessToken, refresh },
            'site-intelligence'
          ).then(async (resp) => {
            const result = await resp?.json();
            return { result: result, status: result?.code ?? resp?.status };
          });
          const resolvedResult = await result;
          if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false)
            return resolvedResult;
        } catch (err) {
          const response = await err?.response?.json();
          setToast({
            show: true,
            type: 'Danger',
            header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
            message: response?.message ?? err.message,
          });
        }
      }
    },
    {
      keepPreviousData: true,
      // Poll every 10 seconds to demonstrate smart sync by default
      // refetchInterval: 10000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  useEffect(() => {
    if (remarkOptionData?.results?.data) {
      setRemarkOptions(remarkOptionData.results.data);
    }
  }, [remarkOptionData]);

  const getRemarkLabel = useCallback(
    (option: any) => {
      const currentLang = option.translations?.find((t: any) => t.language === intl.locale);
      if (currentLang) {
        return currentLang.label;
      }
      const defaultLang = option.translations?.find((t: any) => t.language === 'en');
      if (defaultLang) {
        return defaultLang.label;
      }
      const anyLang = option.translations?.find((t: any) => t.label);
      if (anyLang) {
        return anyLang.label;
      }
      return '';
    },
    [intl.locale]
  );

  const requestData = useMemo(() => {
    const requestId = data?.results?.request_id;
    const requestRemarkOptions = data?.results?.remark_options || [];
    const version = data?.results?.version ?? 1;
    const users = data?.results?.users ?? [];

    return { requestId, requestRemarkOptions, version, users };
  }, [data]);

  const remarkData = useMemo(() => {
    const requestRemarkOptions = requestData.requestRemarkOptions;
    const requestIds = new Set(requestRemarkOptions.map((i) => i.remark_option_id));

    remarkOptions?.sort((a, b) => {
      const aLabel = getRemarkLabel(a);
      const bLabel = getRemarkLabel(b);
      return aLabel.localeCompare(bLabel);
    });

    const allLabelsSet = new Set<string>();
    const all = remarkOptions?.reduce((acc, item) => {
      const label = getRemarkLabel(item);
      if (!allLabelsSet.has(label)) {
        allLabelsSet.add(label);
        acc.push(item);
      }
      return acc;
    }, []);
    const selected = all?.filter((item) => requestIds.has(item.remark_option_id));

    return { all, selected };
  }, [requestData, remarkOptions]);

  const remarkOptionMap = useMemo(() => {
    if (!remarkOptions) return;
    const map = new Map();
    for (const remarkOption of remarkOptions) {
      map.set(remarkOption.remark_option_id, getRemarkLabel(remarkOption));
    }
    return map;
  }, [remarkOptions]);

  const getImageUrl = (imagePath: string | File) =>
    imagePath instanceof File ? URL.createObjectURL(imagePath) : imagePath;

  function handleCopy(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      setToast({
        show: true,
        type: 'Success',
        header: intl.formatMessage({ id: 'MESSAGE.COPIED' }),
        message: intl.formatMessage({ id: 'MESSAGE.CREDENTIALS_COPIED' }),
      });
    });
  }

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
        haveHistory={hasHistory && isEditable}
        onHistory={hasHistory ? () => setShowHistoryModal(true) : undefined}
        headerToolbar={
          isEditable &&
          isDirty && (
            <button
              onClick={(e) => {
                e.currentTarget.blur();
                reset();
              }}
              className='btn btn-sm btn-secondary'
              title='Reset'
            >
              Reset
            </button>
          )
        }
        isEditable={isEditable}
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={submit}
        onReset={reset}
      >
        <div className='card-body p-9'>
          {isEditable ? (
            <EditSiRemarks
              ref={formRef}
              data={remarkData}
              requestData={requestData}
              isSiteSaving={isSiteSaving}
              captures={captures}
              refetchList={refetch}
              handleDirtyChange={handleDirtyChange}
              fieldUpdates={allFieldUpdates}
              setSelectedImagesData={setSelectedImagesData}
              setShowImageModal={setShowImagesModal}
            />
          ) : (
            <UnlabeledBulletColumns
              data={
                remarkData.selected?.map((item: any) => {
                  const remarkCaptureIds = requestData.requestRemarkOptions.find(
                    (option) => option.remark_option_id === item.remark_option_id
                  )?.capture_ids;
                  const thumbnailSize = '1rem';
                  return (
                    <div key={item.remark_option_id} className='d-flex gap-2'>
                      {getRemarkLabel(item)}
                      {remarkCaptureIds?.length > 0 && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          style={{
                            height: thumbnailSize,
                            width: thumbnailSize,
                          }}
                        >
                          <div className='overlay d-inline-block'>
                            <div className='overlay-wrapper'>
                              <i className='fas fa-image text-primary fs-3'></i>
                            </div>
                            <div
                              className='overlay-layer bg-dark bg-opacity-50 rounded-1 cursor-pointer'
                              onClick={() => {
                                setShowImagesModal(true);
                                setSelectedImagesData({
                                  label: getRemarkLabel(item),
                                  captures: remarkCaptureIds.map((captureId: string) => ({
                                    url: captures?.find((c) => c.capture_id === captureId)?.url,
                                    image: getImagePathByCaptureId(captureId, captures),
                                  })),
                                });
                              }}
                              title='View image'
                            >
                              <i className='fas fa-eye text-white fs-3'></i>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }) ?? []
              }
              columnCount={1}
              className={{
                value: 'fs-base',
              }}
            />
          )}
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>

      <ImageListModal
        title={
          selectedImagesData?.label
            ? `Captures for the Remark "${selectedImagesData.label}"`
            : 'Captures'
        }
        imagePaths={selectedImagesData?.captures?.map((capture) => getImageUrl(capture.image))}
        show={showImagesModal}
        onHide={() => setShowImagesModal(false)}
        renderSelectedImageOverlayContent={(selectedImageIndex) => {
          const captureSourceUrl = selectedImagesData?.captures?.[selectedImageIndex]?.url;
          return (
            captureSourceUrl && (
              <div className='position-absolute ps-1 top-100 start-0 w-100 d-flex'>
                <div className='text-truncate text-primary bg-white fw-bolder fs-7'>
                  <a href={captureSourceUrl} target='_blank' rel='noopener noreferrer'>
                    {captureSourceUrl}
                  </a>{' '}
                </div>
                <i
                  className='fs-4 bi bi-files cursor-pointer flex-shrink-0'
                  onClick={() => handleCopy(captureSourceUrl)}
                />
              </div>
            )
          );
        }}
        bodyClassName='position-relative mb-3 text-center'
        restoreFocus={false} // prevent restoring focus to previously focused element on hide
      />

      <HistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
        data={data}
        service_paths={[
          {
            ref_id: data?.results?._id,
            paths: ['remark_options'],
          },
        ]}
        historyReference='SI_REQUEST'
        summaryValueMap={remarkOptionMap}
      />
    </>
  );
}
